import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Modal,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import GetAppIcon from '@mui/icons-material/GetApp';

const SYSTEM_TYPES = new Set(['e2e_notification', 'pinned_message', 'gp2', 'revoked', 'ciphertext']);

function assignPositions(messages) {
  let lastUser = null;
  let position = 'left';
  return messages.map((msg) => {
    const currentUserId = msg.fromMe ? 'You' : (msg.author || 'Unknown');
    if (currentUserId !== lastUser) {
      position = position === 'left' ? 'right' : 'left';
      lastUser = currentUserId;
    }
    return { ...msg, position };
  });
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getExtension(mimeType) {
  if (!mimeType) return '';
  const [typePart] = mimeType.split(';');
  const [mainType, subType] = typePart.split('/');
  let extension = subType || '';
  if (mainType === 'audio') {
    if (subType === 'ogg' || subType === 'opus') extension = 'oga';
    else if (subType === 'mpeg') extension = 'mp3';
  } else if (mainType === 'video') {
    if (subType === 'ogg') extension = 'ogv';
    else if (subType === '3gpp') extension = '3gp';
  }
  return extension;
}

function MediaContent({ msg, apiBaseUrl }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const mediaData = msg.mediaData || msg.media_data || {};
  const filename = mediaData.filename || mediaData.file_name || '';
  const ext = getExtension(mediaData.mimetype || mediaData.mime_type || '');
  const src = filename ? `${apiBaseUrl}/media/${filename}${ext ? '.' + ext : ''}` : null;
  const type = msg.type;

  if (!src) {
    return (
      <Typography variant="caption" color="text.secondary" fontStyle="italic">
        Media not available
      </Typography>
    );
  }

  if (type === 'image' || type === 'sticker') {
    const size = type === 'sticker' ? 128 : 250;
    return (
      <>
        <Box
          component="img"
          src={src}
          alt={filename}
          sx={{ maxWidth: size, maxHeight: size, borderRadius: 1, cursor: 'pointer', display: 'block' }}
          onClick={() => src && setLightboxOpen(true)}
        />
        <Modal open={lightboxOpen} onClose={() => setLightboxOpen(false)}>
          <Box
            sx={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%,-50%)',
              outline: 'none', maxWidth: '90vw', maxHeight: '90vh',
            }}
            onClick={() => setLightboxOpen(false)}
          >
            <Box component="img" src={src} alt={filename} sx={{ maxWidth: '90vw', maxHeight: '90vh' }} />
          </Box>
        </Modal>
      </>
    );
  }
  if (type === 'video') {
    return (
      <Box component="video" src={src} controls sx={{ maxWidth: 250, display: 'block', borderRadius: 1 }} />
    );
  }
  if (type === 'audio' || type === 'ptt') {
    return <Box component="audio" src={src} controls sx={{ display: 'block' }} />;
  }
  if (type === 'document') {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <InsertDriveFileIcon fontSize="small" />
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{filename}</Typography>
        {src && (
          <Box component="a" href={src} download sx={{ display: 'flex', alignItems: 'center' }}>
            <GetAppIcon fontSize="small" />
          </Box>
        )}
      </Box>
    );
  }
  return null;
}

function QuotedMessage({ messageReply }) {
  if (!messageReply) return null;
  return (
    <Box
      sx={{
        borderLeft: '3px solid #aaa',
        bgcolor: '#f0f0f0',
        borderRadius: 1,
        px: 1,
        py: 0.5,
        mb: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {messageReply.author || messageReply.from || ''}
      </Typography>
      <Typography variant="caption" noWrap>
        {messageReply.body || ''}
      </Typography>
    </Box>
  );
}

function ChatBubble({ msg, apiBaseUrl, targetRef }) {
  const isLeft = msg.position === 'left';
  const isTarget = msg.is_target;

  return (
    <Box
      ref={isTarget ? targetRef : undefined}
      display="flex"
      flexDirection="column"
      alignItems={isLeft ? 'flex-start' : 'flex-end'}
      mb={0.5}
    >
      {isTarget && (
        <Chip label="Target" size="small" color="warning" sx={{ mb: 0.5 }} />
      )}
      <Paper
        elevation={1}
        sx={{
          bgcolor: isTarget ? '#fff176' : isLeft ? '#ffffff' : '#dcf8c6',
          maxWidth: '75%',
          borderRadius: 2,
          p: 1,
        }}
      >
        {msg.forwardingScore > 3 && (
          <Typography variant="caption" fontStyle="italic" display="block" color="text.secondary">
            Forwarded many times
          </Typography>
        )}
        {msg.hasQuotedMsg && msg.messageReply && (
          <QuotedMessage messageReply={msg.messageReply} />
        )}
        {(msg.type === 'chat' || !msg.type) ? (
          <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {msg.body || ''}
          </Typography>
        ) : (
          <MediaContent msg={msg} apiBaseUrl={apiBaseUrl} />
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.25}>
          <Typography
            variant="caption"
            sx={{ color: isLeft ? '#1565c0' : '#2e7d32', fontWeight: 500, mr: 1 }}
          >
            {msg.fromMe ? 'You' : (msg.author || msg.from || '')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTime(msg.timestamp)}
          </Typography>
        </Box>
        {msg.reactionData && Object.entries(msg.reactionData).length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
            {Object.entries(msg.reactionData).map(([emoji, count]) => (
              <Chip key={emoji} label={`${emoji} ${count}`} size="small" variant="outlined" />
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default function WhatsAppChat({ messages, isLoading, apiBaseUrl, token }) {
  const targetRef = useRef(null);

  const filtered = (messages || []).filter(
    (m) => m && !SYSTEM_TYPES.has(m.type) && m.timestamp != null
  );
  const positioned = assignPositions(filtered);

  useEffect(() => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [messages]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (!positioned.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="text.secondary">No messages</Typography>
      </Box>
    );
  }

  return (
    <Box p={1}>
      {positioned.map((msg, idx) => (
        <ChatBubble key={msg.msg_id || idx} msg={msg} apiBaseUrl={apiBaseUrl} targetRef={targetRef} />
      ))}
    </Box>
  );
}
